'use client';

import { useState, useEffect } from 'react';
import { varAlpha } from 'minimal-shared/utils';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import ListItemText from '@mui/material/ListItemText';

import { useRouter } from 'src/routes/hooks';
import { newSearchParams } from 'src/routes/paths';

import { useTranslate } from 'src/locales';
import { useGetProducts } from 'src/actions/product';

import { useLogoStore } from 'src/components/logo';
import { EmptyContent } from 'src/components/empty-content';
import { ButtonLoadMore } from 'src/components/custom-buttons';
import { AccountTitle, AccountStatistics } from 'src/components/account-data';

import { ProductList } from '../product-list';
import { ProductFiltersResult } from '../product-filters-result';
import { applyFilter, RenderFilters } from '../components/render-filters';

// ----------------------------------------------------------------------

const renderNotFound = <EmptyContent filled sx={{ py: 10 }} />;

export function ProductShopView({
  sx,
  list,
  seller,
  sortBy,
  products,
  hideShopName,
  searchParams,
}) {
  const router = useRouter();

  const { setPhotoURL } = useLogoStore();

  const { t, currentLang } = useTranslate();

  const [sortby, setSortBy] = useState(sortBy);

  const {
    filters,
    loading,
    showing,
    maxValue,
    setLoading,
    loadingMore,
    allProducts,
    productsEmpty,
    totalProducts,
    otherCondition,
    loadMoreProducts,
  } = useGetProducts(list, sortBy, products, searchParams);

  const { state: currentFilters } = filters;

  const dataFiltered = applyFilter({
    list,
    sortby,
    maxValue,
    inputData: allProducts,
    filters: currentFilters,
  });

  const canReset =
    // currentFilters.shipping ||
    currentFilters.gender.length > 0 ||
    currentFilters.colors.length > 0 ||
    currentFilters.rating !== '' ||
    currentFilters.category !== 'all' ||
    currentFilters.priceRange[0] !== 0 ||
    currentFilters.priceRange[1] !== maxValue;

  const notFound = productsEmpty || (!productsEmpty && !dataFiltered.length && canReset);

  const updateParams = (paramName, value) => {
    if (list === 'shop') {
      if (paramName !== 'sortBy') setLoading();

      const search = newSearchParams({
        ...searchParams,
        country: currentLang.countryName,
        ...(value && { [paramName]: value }),
      });

      router.push(search, { scroll: false });
    }
  };

  const onResetParams = () => {
    const canResetUrl =
      // currentFilters.shipping ||
      currentFilters.category !== 'all' ||
      currentFilters.priceRange[0] !== 0 ||
      currentFilters.priceRange[1] !== maxValue;

    if (list === 'shop' && canResetUrl) {
      setLoading();

      const search = newSearchParams({
        country: currentLang.countryName,
        ...(seller?.id && { seller: seller.id }),
      });

      router.push(search);
    }
  };

  useEffect(() => {
    if (!hideShopName) setPhotoURL(seller?.photoURL);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seller]);

  const renderTitle = () => (
    <Stack spacing={2} direction="row" sx={{ px: { xs: 2, sm: 0 }, mt: { xs: 3, sm: 0 } }}>
      {seller.id && (
        <Avatar
          src={seller.photoURL}
          sx={{ my: 'auto', width: 30, height: 30, display: { sm: 'none' } }}
        />
      )}

      <ListItemText
        primary={
          seller.id ? (
            <AccountTitle
              user={seller}
              slotProps={{ title: { sx: { typography: { xs: 'h6', sm: 'h4' } } } }}
            />
          ) : (
            <Typography
              component="div"
              sx={{ textTransform: 'capitalize', typography: { xs: 'h6', sm: 'h4' } }}
            >
              {t(list)}
            </Typography>
          )
        }
        secondary={
          seller.id && (
            <AccountStatistics
              totalSales={seller?.totalSales}
              totalRating={seller?.totalRating}
              totalFollowers={seller?.totalFollowers}
              sx={{ mt: 0.5 }}
            />
          )
        }
        slotProps={{
          primary: { component: 'div' },
          secondary: { component: 'div' },
        }}
      />
    </Stack>
  );

  const renderResults = () => (
    <ProductFiltersResult
      filters={filters}
      maxValue={maxValue}
      updateParams={updateParams}
      onResetParams={onResetParams}
      totalResults={dataFiltered.length}
    />
  );

  return (
    <Container
      sx={[{ mb: { xs: 9, sm: 12 }, px: { xs: 0, sm: 2 } }, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      {!hideShopName && <Box sx={{ display: { sm: 'none' } }}>{renderTitle()}</Box>}

      <Stack
        spacing={{ xs: 2, sm: 0.5 }}
        className="search__input__sticky"
        sx={[
          (theme) => ({
            my: 0,
            top: 0,
            py: 1.5,
            px: { xs: hideShopName ? 0 : 1.5, md: 1.5 },
            [theme.breakpoints.up('md')]: { my: 4, px: 2, pt: 1, pb: 2, top: 72 },
            ...theme.mixins.bgBlur({
              color: varAlpha(theme.vars.palette.background.defaultChannel, 0.8),
            }),
          }),
        ]}
      >
        <RenderFilters
          list={list}
          seller={seller}
          sortBy={sortBy}
          filters={filters}
          maxValue={maxValue}
          canReset={canReset}
          setSortBy={setSortBy}
          renderTitle={renderTitle}
          hideShopName={hideShopName}
          updateParams={updateParams}
          onResetParams={onResetParams}
        />

        {canReset && renderResults()}
      </Stack>

      {notFound && renderNotFound}

      <ProductList
        loading={loading}
        products={dataFiltered}
        loadingMore={loadingMore}
        searchParams={searchParams}
      />

      <ButtonLoadMore
        showing={showing}
        loading={loadingMore}
        total={totalProducts}
        onClick={loadMoreProducts}
        otherCondition={otherCondition}
        sx={{ mt: 8 }}
      />
    </Container>
  );
}
